/**
 * DocumentAnalyzer
 * Dialog for uploading and analyzing documents (PDF, DOCX, XLSX)
 */

import React, { useState, useRef, useCallback } from 'react'
import { formatDate } from '@/lib/jurisdiction/jurisdiction-config'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FileUp,
  Loader2,
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle2,
  X,
  Upload,
  Sparkles,
  Clock,
  Database,
} from 'lucide-react'
import { copilotApi, type DocumentAnalysisResult } from '@/services/copilot.service'
import {
  ingestKnowledgeDoc,
  useKnowledgeDocs,
  type KnowledgeDocScope,
} from '@/services/knowledge-docs.service'
import { getRagSessionId } from '@/lib/rag-session'

// ── Props ────────────────────────────────────────────────────────────────────

export interface DocumentAnalyzerProps {
  moduleContext: string
  onExtracted?: (data: DocumentAnalysisResult) => void
  trigger?: React.ReactNode
}

// ── Scope badge (shared rendering for docs list + ingest confirmation) ───────

export function ScopeBadge({ scope, expiresAt }: { scope?: string | null; expiresAt?: string | null }) {
  const isSession = scope === 'session'
  return (
    <Badge
      variant="outline"
      data-testid={`scope-badge-${isSession ? 'session' : 'permanent'}`}
      className={
        isSession
          ? 'gap-1 border-amber-500/40 bg-amber-500/10 text-amber-600 text-[10px]'
          : 'gap-1 border-emerald-500/40 bg-emerald-500/10 text-emerald-600 text-[10px]'
      }
      title={
        isSession
          ? `Document temporaire — purgé automatiquement${expiresAt ? ` (expire ${formatDate(expiresAt, { dateStyle: 'short', timeStyle: 'short' })})` : ''}`
          : 'Document permanent — base de connaissances du tenant'
      }
    >
      {isSession ? <Clock className="h-3 w-3" /> : <Database className="h-3 w-3" />}
      {isSession ? 'Session' : 'Permanent'}
    </Badge>
  )
}

// ── Accepted file types ──────────────────────────────────────────────────────

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.ms-excel',
]

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.xlsx,.doc,.xls'

const IMPORTANCE_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
}

// ── Component ────────────────────────────────────────────────────────────────

interface IngestState {
  status: 'idle' | 'indexing' | 'done' | 'error'
  scope?: KnowledgeDocScope
  docId?: string
  expiresAt?: string | null
  chunks?: number
}

export function DocumentAnalyzer({ moduleContext, onExtracted, trigger }: DocumentAnalyzerProps) {
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<DocumentAnalysisResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // TRUST layer (subsystem J): temporary (session) vs permanent (KB) scope.
  const [scope, setScope] = useState<KnowledgeDocScope>('permanent')
  const [ingest, setIngest] = useState<IngestState>({ status: 'idle' })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sessionId = getRagSessionId()
  const docsQuery = useKnowledgeDocs(sessionId, open)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      setError('Format non supporté. Utilisez PDF, DOCX ou XLSX.')
      return
    }

    if (selectedFile.size > 20 * 1024 * 1024) {
      setError('Le fichier dépasse la limite de 20 Mo.')
      return
    }

    setFile(selectedFile)
    setError(null)
    setResult(null)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      if (!ACCEPTED_TYPES.includes(droppedFile.type)) {
        setError('Format non supporté. Utilisez PDF, DOCX ou XLSX.')
        return
      }
      setFile(droppedFile)
      setError(null)
      setResult(null)
    }
  }, [])

  /**
   * TRUST layer (subsystem J): make the document RAG-retrievable with the
   * chosen scope. Session docs go to the isolated namespace
   * `tmp:{tenant}:{sessionId}` and auto-purge (TTL 24h). Fail-soft: an
   * indexing failure never fails the analysis itself.
   */
  const indexDocument = async (f: File) => {
    setIngest({ status: 'indexing', scope })
    try {
      // Reuse the existing extract-file endpoint to get the raw text.
      const extracted = await copilotApi.extractFile(f, undefined, moduleContext)
      const text = (extracted?.document_text || '').trim()
      if (!text) throw new Error('empty text')
      const res = await ingestKnowledgeDoc({
        title: f.name.replace(/\.[a-z0-9]+$/i, ''),
        text,
        filename: f.name,
        scope,
        session_id: scope === 'session' ? sessionId : undefined,
        metadata: { module: moduleContext },
      })
      setIngest({
        status: 'done',
        scope: res.scope,
        docId: res.doc_id,
        expiresAt: res.expires_at ?? null,
        chunks: res.chunks,
      })
      docsQuery.refetch()
    } catch {
      setIngest({ status: 'error', scope })
    }
  }

  const handleAnalyze = async () => {
    if (!file) return

    setIsAnalyzing(true)
    setProgress(0)
    setError(null)

    try {
      // Index into the RAG store (scope-aware) in parallel with the analysis.
      const indexing = indexDocument(file)
      const analysisResult = await copilotApi.analyzeDocument(
        file,
        moduleContext,
        (p) => setProgress(p)
      )
      setResult(analysisResult)
      onExtracted?.(analysisResult)
      await indexing.catch(() => undefined)
    } catch (err) {
      setError('Erreur lors de l\'analyse. Veuillez réessayer.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleReset = () => {
    setFile(null)
    setResult(null)
    setError(null)
    setProgress(0)
    setIngest({ status: 'idle' })
  }

  const handleClose = () => {
    setOpen(false)
    // Keep results so user can re-open
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <FileUp className="h-4 w-4" />
            Analyser un document
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[700px] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Analyse de document IA
          </DialogTitle>
          <DialogDescription>
            Uploadez un document (PDF, DOCX, XLSX) pour en extraire les clauses clés, contrôles et obligations.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
          {/* Upload Zone */}
          {!result && (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                file ? 'border-primary/50 bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/30'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {!file ? (
                <div className="space-y-3">
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <div>
                    <p className="text-sm font-medium">Glissez un fichier ici ou cliquez pour sélectionner</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOCX, XLSX - Max 20 Mo
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Sélectionner un fichier
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div className="text-left">
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} Mo
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleReset}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Scope toggle — TRUST layer (subsystem J) */}
          {!result && (
            <div className="rounded-lg border p-3 space-y-2" data-testid="rag-scope-toggle">
              <p className="text-xs font-medium text-muted-foreground">
                Portée du document dans la base RAG
              </p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="scope-option-permanent"
                  onClick={() => setScope('permanent')}
                  aria-pressed={scope === 'permanent'}
                  className={`flex items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors ${
                    scope === 'permanent'
                      ? 'border-emerald-500/60 bg-emerald-500/10'
                      : 'border-muted hover:border-emerald-500/30'
                  }`}
                >
                  <Database className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span>
                    <span className="block font-medium">Permanent</span>
                    <span className="block text-[11px] text-muted-foreground">
                      Base de connaissances
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  data-testid="scope-option-session"
                  onClick={() => setScope('session')}
                  aria-pressed={scope === 'session'}
                  className={`flex items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors ${
                    scope === 'session'
                      ? 'border-amber-500/60 bg-amber-500/10'
                      : 'border-muted hover:border-amber-500/30'
                  }`}
                >
                  <Clock className="h-4 w-4 shrink-0 text-amber-600" />
                  <span>
                    <span className="block font-medium">Temporaire (session)</span>
                    <span className="block text-[11px] text-muted-foreground">
                      Purgé automatiquement
                    </span>
                  </span>
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {scope === 'session'
                  ? 'Le document ne sera consultable que dans cette session et sera automatiquement purgé (24 h max). Il ne rejoint jamais la base de connaissances permanente.'
                  : 'Le document rejoint la base de connaissances permanente du tenant et reste consultable par les analyses IA.'}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg p-3">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {/* RAG indexing status */}
          {ingest.status !== 'idle' && (
            <div
              className="flex items-center gap-2 text-xs rounded-lg border p-2"
              data-testid="rag-ingest-status"
            >
              {ingest.status === 'indexing' && (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  Indexation RAG ({ingest.scope === 'session' ? 'temporaire' : 'permanent'})…
                </>
              )}
              {ingest.status === 'done' && (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  Document indexé ({ingest.chunks} extrait{(ingest.chunks ?? 0) > 1 ? 's' : ''})
                  <ScopeBadge scope={ingest.scope} expiresAt={ingest.expiresAt} />
                </>
              )}
              {ingest.status === 'error' && (
                <>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                  Indexation RAG indisponible pour ce fichier (l'analyse reste valable).
                </>
              )}
            </div>
          )}

          {/* Progress */}
          {isAnalyzing && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                Analyse en cours...
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Analyze Button */}
          {file && !result && !isAnalyzing && (
            <Button onClick={handleAnalyze} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Lancer l'analyse
            </Button>
          )}

          {/* Results */}
          {result && (
            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-4 pr-4">
                {/* Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Résumé du document
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{result.summary}</p>
                    <Badge variant="outline" className="mt-2 text-xs">
                      Type: {result.documentType}
                    </Badge>
                  </CardContent>
                </Card>

                {/* Key Clauses */}
                {result.keyClauses.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4 text-amber-500" />
                        Clauses clés ({result.keyClauses.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {result.keyClauses.map((clause, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm border-l-2 border-muted pl-3 py-1">
                          <Badge className={`shrink-0 text-[10px] ${IMPORTANCE_COLORS[clause.importance]}`}>
                            {clause.importance}
                          </Badge>
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground">{clause.section}</p>
                            <p className="text-sm">{clause.clause}</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Mapped Controls */}
                {result.mappedControls.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-500" />
                        Contrôles identifiés ({result.mappedControls.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {result.mappedControls.map((ctrl, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm bg-muted/30 rounded p-2">
                          <Badge variant="secondary" className="shrink-0 text-[10px]">
                            {ctrl.controlId}
                          </Badge>
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{ctrl.controlName}</p>
                            <p className="text-xs text-muted-foreground">{ctrl.mapping}</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Obligations */}
                {result.identifiedObligations.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Obligations ({result.identifiedObligations.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {result.identifiedObligations.map((obl, i) => (
                        <div key={i} className="flex items-start justify-between text-sm border-b last:border-0 pb-2 last:pb-0">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm">{obl.obligation}</p>
                            {obl.responsible && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Responsable: {obl.responsible}
                              </p>
                            )}
                          </div>
                          {obl.dueDate && (
                            <Badge variant="outline" className="shrink-0 ml-2 text-[10px]">
                              {obl.dueDate}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex gap-2 pb-2">
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    Analyser un autre document
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}

          {/* Indexed documents (permanent + this session) with scope badges */}
          {!result && (docsQuery.data?.docs?.length ?? 0) > 0 && (
            <div className="shrink-0 rounded-lg border p-2" data-testid="rag-docs-list">
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                Documents indexés ({docsQuery.data?.docs.length})
              </p>
              <div className="max-h-28 space-y-1 overflow-y-auto pr-1">
                {docsQuery.data?.docs.slice(0, 20).map((d) => (
                  <div
                    key={d.doc_id}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <span className="min-w-0 truncate" title={d.title ?? d.doc_id}>
                      {d.title ?? d.doc_id}
                    </span>
                    <ScopeBadge scope={d.scope} expiresAt={d.expires_at} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentAnalyzer
